import React, { useContext, useEffect, useMemo, useState } from 'react';

import * as S from './styles';

import OutboxIcon from '@mui/icons-material/Outbox';
import ListIcon from '@mui/icons-material/List';

import { useTheme } from 'styled-components';
import { ButtonSimple } from '../../../../../../../../Buttons';
import { myCoursesServiceNewStudent } from '../../../../../../../../../../services/newStudent';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../../../../../../ProviderNewStudent';
import { ISubjectContentData } from '../../../../../..';
import Divider from '../../../../../../../../../common/Divider';
import {
  IPillarTcc,
  IRulesToSendTcc,
  IShowInternship,
} from '../../../../../../../../../../services/newStudent/myCourses/myCourses.service';
import ButtonIcon from '../../../../../../../../Buttons/ButtonIcon';
import TableBasic, { ITableData } from '../../../../../../../../Table';
import {
  formatDate,
  numberToString,
} from '../../../../../../../../../../util/common/appUtil';
import RegisterAcademicProject from '../RegisterAcademicProject';
import AcademicProjectsHistoric from '../AcademicProjectsHistoric';
import { handleMaxWidth } from '../../../../../../../../../../util/newStudent';
import { IClassInfo } from '../../../../../../../../../../pages/newStudent/MyCoursesNewStudent/components/Subjects';
import AcademicProjectPillar from '../AcademicProjectPillar';
import DownloadIcon from '@mui/icons-material/Download';
import {
  AcademicProjectKind,
  academicProjectLabel,
} from '../../../../../../../../../../util/newStudent/academicProject';

interface Props {
  info: ISubjectContentData;
  classInfo: IClassInfo;
  projectKind: AcademicProjectKind;
}
export default function AcademicProject({
  info,
  classInfo,
  projectKind,
}: Props) {
  const { setIsLoading, student } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const projectLabel = academicProjectLabel(
    projectKind,
    info.purposeText,
    info.title,
  );
  const isTcc = projectKind === 'tcc';

  const [loadInternship, setLoadInternship] = useState<boolean>(true);
  const [teacherObservation, setTeacherObservation] = useState<string>('');
  const [statusWorking, setStatusWorking] = useState<{
    status: string;
    grade: number;
  }>({ status: 'Não informado', grade: 0 });

  const [academicProjectData, setAcademicProjectData] =
    useState<IShowInternship[]>();
  const [whoShow, setWhoShow] = useState<
    '' | 'register' | 'historic' | 'list' | 'pillar'
  >('');
  const [historicIdToShow, setHistoricIdToShow] = useState<number>();
  const [canShowPayment, setCanShowPayment] = useState<boolean | undefined>(
    false,
  );
  const [rulesToSendTcc, setRulesToSendTcc] = useState<IRulesToSendTcc>();
  const [pillar, setPillar] = useState<IPillarTcc[]>();
  const [tccObservationDismissal, setTccObservationDismissal] =
    useState<string[]>();

  const [options, setOptions] = useState<{
    dismissalTcc?: boolean;
    finalCorrectionTcc?: boolean;
    orientationTcc?: boolean;
    overview?: string[];
  }>();

  const theme = useTheme();

  const applyAcademicProjectOptions = (data: {
    paymentType?: boolean;
    dismissalTcc?: boolean;
    finalCorrectionTcc?: boolean;
    orientationTcc?: boolean;
    overview?: string[];
    tccObservationDismissal?: string[];
  }) => {
    setCanShowPayment(data.paymentType);
    setOptions({
      dismissalTcc: data.dismissalTcc,
      finalCorrectionTcc: data.finalCorrectionTcc,
      orientationTcc: data.orientationTcc,
      overview: data.overview,
    });
    setTccObservationDismissal(data.tccObservationDismissal);
  };

  const refreshAcademicProjectOptions = async () => {
    try {
      setIsLoading(true);
      const { data } = await myCoursesServiceNewStudent.showAcademicProject({
        id: classInfo.courseId,
        disciplineId: classInfo.id,
        contentId: info.contentId,
        resourceId: info.resourceId ?? 0,
        assessmentWork: info.type === 'assessmentWork',
      });
      applyAcademicProjectOptions(data);
    } catch (error) {
      throw new Error(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRegister = async () => {
    await refreshAcademicProjectOptions();
    setWhoShow('register');
  };

  const loadAcademicProject = async () => {
    try {
      const { result, data } =
        await myCoursesServiceNewStudent.showAcademicProject({
          id: classInfo.courseId,
          disciplineId: classInfo.id,
          contentId: info.contentId,
          resourceId: info.resourceId ?? 0,
          assessmentWork: info.type === 'assessmentWork',
        });

      applyAcademicProjectOptions(data);
      if (result && data.project) {
        setAcademicProjectData(data.project);

        setWhoShow('list');
      } else {
        setWhoShow('register');
      }
    } catch (error) {
      throw new Error(String(error));
    } finally {
      setLoadInternship(false);
    }
  };

  const loadTccPillar = async () => {
    const { result, data } = await myCoursesServiceNewStudent.tccPillar({
      id: classInfo.courseId,
      disciplineId: Number(classInfo.id),
      contentId: Number(info.contentId),
      resourceId: Number(info.resourceId ?? 0),
    });
    if (result) {
      if (data.completed) {
        loadAcademicProject();
      } else {
        setWhoShow('pillar');
        setPillar(data.pillars);
        setRulesToSendTcc(data.requirements);
      }
    }
  };

  useEffect(() => {
    setLoadInternship(true);
    setAcademicProjectData(undefined);
    setWhoShow('');
    setHistoricIdToShow(undefined);
    setTeacherObservation('');
    setStatusWorking({ status: 'Não informado', grade: 0 });
  }, [info.resourceId, info.contentId, info.type]);

  useEffect(() => {
    if (!loadInternship) return;

    const load = async () => {
      try {
        setIsLoading(true);
        if (isTcc) {
          await loadTccPillar();
        } else {
          await loadAcademicProject();
        }
      } catch (error) {
        throw new Error(String(error));
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [loadInternship, isTcc, info.resourceId, info.contentId, info.type]);

  const changeIdHistoricSelected = useMemo(() => {
    return academicProjectData?.find(
      (item) => item.academicProjectId === historicIdToShow,
    );
  }, [academicProjectData, historicIdToShow]);

  const titles = ['Data', 'Tema', 'Status'];

  const hasAnyHistoric = academicProjectData?.some(
    (item) => item?.historic && item.historic.length > 0,
  );

  if (hasAnyHistoric) {
    titles.push('Histórico');
  } else {
    titles.push('Arquivo');
  }

  const dataTable: ITableData = useMemo(() => {
    return {
      title: titles,
      content: academicProjectData
        ? academicProjectData.map((item) => {
            const hasHistoric = item?.historic && item.historic.length > 0;
            const hasFile = item?.link;

            setTeacherObservation(item.teacherObservation ?? '');
            setStatusWorking({
              status: item.gradeStatus ?? 'Não informado',
              grade: item.grade ?? 0,
            });

            return {
              date: { item: <S.H6>{formatDate(item?.date)}</S.H6> },
              subject: { item: <S.H6>{item?.subject}</S.H6> },
              status: { item: <S.H6>{item?.status}</S.H6> },
              historic: {
                item: hasHistoric ? (
                  <S.Div>
                    <ButtonIcon
                      color="primary"
                      icon={<ListIcon fontSize="large" />}
                      onClick={() => {
                        setHistoricIdToShow(Number(item?.academicProjectId));
                        setWhoShow('historic');
                      }}
                    />
                  </S.Div>
                ) : hasFile ? (
                  <ButtonIcon
                    color="success"
                    icon={<DownloadIcon fontSize="large" />}
                    onClick={() => window.open(item.link)}
                  />
                ) : (
                  <S.H6>-</S.H6>
                ),
              },
            };
          })
        : [],
    };
  }, [academicProjectData]);

  return (
    <S.Container>
      <S.PdfDiv className="archive_content">
        <S.TitlePdfDiv>
          <S.TitleIconPdfDiv>
            <OutboxIcon
              style={{
                width: '40px',
                height: '40px',
                color: theme.colors?.danger,
              }}
            />
            <S.TitlePdfH5>{info.title}</S.TitlePdfH5>
          </S.TitleIconPdfDiv>

          {academicProjectData &&
            whoShow === 'historic' &&
            changeIdHistoricSelected?.canCreateHistoric &&
            !handleMaxWidth(theme.size?.medium) && (
              <ButtonSimple
                color="success"
                widthSize="xlarge"
                onClick={handleOpenRegister}
              >
                Cadastrar Histórico
              </ButtonSimple>
            )}
        </S.TitlePdfDiv>
      </S.PdfDiv>
      <Divider padding />
      {academicProjectData && whoShow === 'list' && (
        <S.DivContent>
          {hasAnyHistoric ? (
            <S.H6>
              Prezado aluno, para acompanhar todo o processo de seu{' '}
              {projectLabel}, clique no botão
              &quot;Histórico&quot;
            </S.H6>
          ) : (
            <S.H6>
              Prezado aluno, clique no botão &quot;Arquivo&quot; para baixar o
              trabalho enviado
            </S.H6>
          )}
          {handleMaxWidth(theme.size?.medium) ? (
            academicProjectData.map((item, index) => (
              <>
                <S.DivTableMobile key={index}>
                  <Divider padding />
                  <S.KeyValue>
                    <S.Key>Data: </S.Key>
                    <S.Value>{formatDate(item?.date)}</S.Value>
                  </S.KeyValue>
                  <S.KeyValue>
                    <S.Key>Tema: </S.Key>
                    <S.Value>{item?.subject}</S.Value>
                  </S.KeyValue>
                  <S.KeyValue>
                    <S.Key>Status: </S.Key>
                    <S.Value>{item?.status}</S.Value>
                  </S.KeyValue>
                  <S.KeyValue>
                    <S.Key>Histórico: </S.Key>
                    <S.Value>
                      <ButtonIcon
                        color="primary"
                        icon={<ListIcon fontSize="large" />}
                        onClick={() => {
                          setHistoricIdToShow(Number(item?.academicProjectId));
                          setWhoShow('historic');
                        }}
                      />
                    </S.Value>
                  </S.KeyValue>
                </S.DivTableMobile>

                {teacherObservation && (
                  <>
                    <S.DivNota>
                      <S.DivNotaTitle>
                        <S.H6Question>Status: </S.H6Question>
                      </S.DivNotaTitle>
                      <S.H6Response>{statusWorking.status}</S.H6Response>
                    </S.DivNota>

                    <S.DivNota>
                      <S.DivNotaTitle>
                        <S.H6Question>Nota: </S.H6Question>
                      </S.DivNotaTitle>
                      <S.H6Response>
                        {numberToString(String(statusWorking.grade))}
                      </S.H6Response>
                    </S.DivNota>

                    <S.H6Question>Observação do Professor</S.H6Question>
                    <S.H6Response>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: teacherObservation,
                        }}
                      ></div>
                    </S.H6Response>
                  </>
                )}
              </>
            ))
          ) : (
            <S.DivTable>
              <TableBasic data={dataTable} />
              {teacherObservation && (
                <>
                  <S.DivNota>
                    <S.DivNotaTitle>
                      <S.H6Question>Status: </S.H6Question>
                    </S.DivNotaTitle>
                    <S.H6Response>{statusWorking.status}</S.H6Response>
                  </S.DivNota>

                  <S.DivNota>
                    <S.DivNotaTitle>
                      <S.H6Question>Nota: </S.H6Question>
                    </S.DivNotaTitle>
                    <S.H6Response>
                      {numberToString(String(statusWorking.grade))}
                    </S.H6Response>
                  </S.DivNota>

                  <S.H6Question>Observação do Professor</S.H6Question>
                  <S.H6Response>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: teacherObservation,
                      }}
                    ></div>
                  </S.H6Response>
                </>
              )}
            </S.DivTable>
          )}
        </S.DivContent>
      )}
      {whoShow === 'register' && (
        <RegisterAcademicProject
          academicProjectData={academicProjectData?.[0]}
          name={student.dataStudent.name}
          setWhoShow={setWhoShow}
          setLoadAcademicProjectData={setLoadInternship}
          info={info}
          classInfo={classInfo}
          projectKind={projectKind}
          options={options}
          tccObservationDismissal={tccObservationDismissal}
          canShowPayment={canShowPayment}
        />
      )}
      {whoShow === 'historic' && (
        <S.DivHistoricMobile>
          <AcademicProjectsHistoric
            data={changeIdHistoricSelected}
            setWhoShow={setWhoShow}
            resourceId={info?.resourceId}
          />
          {handleMaxWidth(theme.size?.medium) && (
            <S.DivButton>
              <ButtonSimple
                color="primary"
                outline
                onClick={() => setWhoShow('list')}
                widthSize="small"
              >
                Voltar
              </ButtonSimple>
              <ButtonSimple
                color="success"
                widthSize="xlarge"
                onClick={handleOpenRegister}
              >
                Cadastrar Histórico
              </ButtonSimple>
            </S.DivButton>
          )}
        </S.DivHistoricMobile>
      )}
      {whoShow === 'pillar' && (
        <AcademicProjectPillar rules={rulesToSendTcc} pillar={pillar} />
      )}
    </S.Container>
  );
}
